/*
* ## RESPONSIBILITIES
*
* * ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - No framework members are used in this class. It only works with integer ranges.
*
* ## NOTES:
*
* This class is very much an experiment, so use this as reference for your own code 🙂
*
**/

import type { IntRange } from '../utils/IntRange'
import { IntSequence } from '../utils/IntSequence'


export class CirrusReaderReadingStats {
    private _wordSequence: IntSequence | null = null;
    private _totalNumberOfWords: number | null = null;
    private _previousRange: IntRange | null = null;
    private _latestRangeChangedTimestamp: number | undefined;
    private _wordReadingRateHistory: number[][] = [];

    setPublicationLength(numberOrWords: number): void {
        this._totalNumberOfWords = numberOrWords;
        this._wordSequence = new IntSequence(numberOrWords);
    }

    getPublicationLength(): number | null {
        return this._totalNumberOfWords;
    }

    addWordRange(range: IntRange): void {
        this._updateAverageWordsPerMinute(this._previousRange || range);
        this._previousRange = range;
        this._wordSequence?.addRange(range.start, range.end);
    }

    invalidateLatestWordRange(): void {
        this._wordReadingRateHistory.pop();
    }

    getNumberOfWordsRead(): number {
        const readRanges = this._wordSequence?.getEntries() as IntRange[];
        let wordCount: number = 0;

        readRanges?.forEach((range) => {
            wordCount += range.end - range.start;
        });

        return wordCount;
    }

    getPercentRead(): number | undefined {
        if (this._totalNumberOfWords) {
            const wordCount = this.getNumberOfWordsRead();
            return wordCount / this._totalNumberOfWords * 100;
        }
        return undefined;
    }

    getRangesNotRead(): IntRange[] | undefined {
        return this._wordSequence?.getEmptySlots();
    }

    getRangesRead(): IntRange[] | undefined {
        return this._wordSequence?.getEntries();
    }

    getAverageWordsPerMinute(): number {

        let averagesSum = 0;
        this._wordReadingRateHistory.forEach((historyItem: number[]) => {
            averagesSum += (historyItem[1] ? historyItem[1] : 0);
        });

        return averagesSum / this._wordReadingRateHistory.length;
    }

    getLatestAverageWordsPerMinute(): number {
        const history = this._wordReadingRateHistory.length - 1;
        return this._wordReadingRateHistory[history] && this._wordReadingRateHistory[history][1] 
            ? this._wordReadingRateHistory[history][1] 
            : 0;
    }

    private _updateAverageWordsPerMinute(wordRange: IntRange): void {
        const nowTimestampInSeconds = Date.now() / 1000;
        const latestRangeChangedTimestamp = this._latestRangeChangedTimestamp ?
            this._latestRangeChangedTimestamp :
            nowTimestampInSeconds;
        const secondsElapsedSinceLastChange = nowTimestampInSeconds - latestRangeChangedTimestamp > 0 ?
            nowTimestampInSeconds - latestRangeChangedTimestamp :
            1;
        this._latestRangeChangedTimestamp = nowTimestampInSeconds;
        const averageWordsPerMinute = wordRange.length / (secondsElapsedSinceLastChange / 60);
        this._wordReadingRateHistory.push([nowTimestampInSeconds, averageWordsPerMinute]);
    }

}
